// Canonical snake_case field_name keys shared by Jedi_HR and Onboarding Flow (not UI labels).
// Mapping: Jedi_HR UI key ↔ one or more canonical field_names ↔ Onboarding Flow.
// Combined Jedi_HR fields (full names) expand to multiple keys with the same field_remark.
// Address fields map one-to-one so permanent and current stay independent.
// Canonical field_name registry by approval_feedback section (source of truth).
export const ONBOARDING_FEEDBACK_CANONICAL_FIELDS = {
  personal: [
    "aadhaar_number",
    "aadhaar_card",
    "first_name",
    "middle_name",
    "last_name",
    "relationship",
    "father_first_name",
    "father_middle_name",
    "father_last_name",
    "photo",
    "date_of_birth",
    "gender",
    "phone_number",
    "alternative_phone_number",
    "email_address",
    "nationality",
    "marital_status",
  ],
  address: [
    "current_address_house",
    "current_address_street",
    "current_address_area",
    "current_address_landmark",
    "current_address_city",
    "current_address_state",
    "current_address_pincode",
    "permanent_address_house",
    "permanent_address_street",
    "permanent_address_area",
    "permanent_address_landmark",
    "permanent_address_city",
    "permanent_address_state",
    "permanent_address_pincode",
    "same_as_permanent",
  ],
  education: [
    "highest_qualification",
    "qualification",
    "diploma",
    "degree",
    "degree_stream",
    "board",
    "affiliating_body",
    "university",
    "school",
    "college_institute",
    "start_date",
    "graduation_date",
    "marksheet",
    "certificate",
    "consolidated_marksheet",
    "graduation_certificate",
    "cgpa_percentage",
    "maximum_cgpa_percentage",
  ],
  employment: [
    "uan",
    "company",
    "designation",
    "start_date",
    "last_date",
    "this_is_my_last_experience",
    "resignation_letter",
    "relieving_letter",
    "form2",
    "form11",
    "authorization",
    "epfo_passbook",
    "formf",
  ],
  bank: [
    "pan_card",
    "pan",
    "name_as_per_pan",
    "cancelled_cheque_bank_statement",
    "account_number",
    "ifsc",
    "account_type",
    "bank_details_confirmed",
    "name_as_per_bank",
    "branch_name",
    "bank_name",
    "branch_address",
  ],
  insurance: [
    "authorization",
    "nominee_first_name",
    "nominee_middle_name",
    "nominee_last_name",
    "nominee_relationship",
    "nominee_date_of_birth",
    "blood_group",
    "emergency_contact_relationship",
    "emergency_contact_first_name",
    "emergency_contact_middle_name",
    "emergency_contact_last_name",
    "emergency_contact_phone_number",
    "emergency_contact_alternative_phone_number",
    "emergency_contact_email_address",
  ],
  declaration: [
    "declaration",
    "letter_of_authorisation",
    "code_of_conduct",
    "it_policy",
    "posh_policy",
  ],
};

/**
 * Jedi_HR UI field / document key → one or more canonical field_name values.
 * Arrays are used when a combined Jedi field maps to multiple Onboarding fields
 * (same field_remark is sent for each canonical key on save).
 */
export const JEDI_UI_TO_CANONICAL_FIELD_NAMES = {
  // —— Personal ——
  aadhar_number: ["aadhaar_number"],
  aadhaar_number: ["aadhaar_number"],
  first_name: ["first_name"],
  middle_name: ["middle_name"],
  last_name: ["last_name"],
  relationship: ["relationship"],
  father_first_name: ["father_first_name"],
  father_middle_name: ["father_middle_name"],
  father_last_name: ["father_last_name"],
  date_of_birth: ["date_of_birth"],
  gender: ["gender"],
  phone_number: ["phone_number"],
  alternative_phone_number: ["alternative_phone_number"],
  email_address: ["email_address"],
  nationality: ["nationality"],
  marital_status: ["marital_status"],
  "doc-aadhaar": ["aadhaar_card"],
  "doc-aadhar": ["aadhaar_card"],
  "doc-aadhaar-photo": ["aadhaar_card"],
  "doc-profile-photo": ["photo"],
  "doc-photo": ["photo"],

  // —— Address (one UI field → one permanent or current canonical key) ——
  permanent_address_house: ["permanent_address_house"],
  permanent_address_street: ["permanent_address_street"],
  permanent_address_area: ["permanent_address_area"],
  permanent_address_landmark: ["permanent_address_landmark"],
  permanent_address_city: ["permanent_address_city"],
  permanent_address_state: ["permanent_address_state"],
  permanent_address_pincode: ["permanent_address_pincode"],
  current_address_house: ["current_address_house"],
  current_address_street: ["current_address_street"],
  current_address_area: ["current_address_area"],
  current_address_landmark: ["current_address_landmark"],
  current_address_city: ["current_address_city"],
  current_address_state: ["current_address_state"],
  current_address_pincode: ["current_address_pincode"],
  same_as_current: ["same_as_permanent"],

  // —— Education ——
  highest_qualification: ["highest_qualification"],
  qualification: ["qualification"],
  diploma: ["diploma"],
  degree: ["degree"],
  degree_stream: ["degree_stream"],
  board: ["board"],
  affiliating_body: ["affiliating_body"],
  university: ["university"],
  school: ["school"],
  college: ["college_institute"],
  college_institute: ["college_institute"],
  start_date: ["start_date"],
  graduation_date: ["graduation_date"],
  // CGPA/Percentage flag also applies the same remark to Maximum CGPA/Percentage
  cgpa_percentage: ["cgpa_percentage", "maximum_cgpa_percentage"],
  maximum_cgpa_percentage: ["maximum_cgpa_percentage"],

  // —— Employment ——
  uan: ["uan"],
  company: ["company"],
  designation: ["designation"],
  last_date: ["last_date"],
  is_last_experience: ["this_is_my_last_experience"],
  current_check: ["this_is_my_last_experience"],
  "doc-resignation": ["resignation_letter"],
  "doc-relieving": ["relieving_letter"],
  "doc-epfo": ["epfo_passbook"],
  "doc-form2": ["form2"],
  "doc-form11": ["form11"],
  "doc-pf-authorization": ["authorization"],
  "doc-formf": ["formf"],

  // —— Bank ——
  pan_number: ["pan"],
  account_holder_name: ["name_as_per_pan"],
  name_as_per_pan: ["name_as_per_pan"],
  name_as_per_bank: ["name_as_per_bank"],
  account_number: ["account_number"],
  ifsc_code: ["ifsc"],
  ifsc: ["ifsc"],
  account_type: ["account_type"],
  bank_name: ["bank_name"],
  branch_name: ["branch_name"],
  branch_address: ["branch_address"],
  bank_details_confirmed: ["bank_details_confirmed"],
  "doc-pan": ["pan_card"],
  "doc-bank": ["cancelled_cheque_bank_statement"],
  "doc-cheque": ["cancelled_cheque_bank_statement"],

  // —— Insurance (combined full names → First/Middle/Last) ——
  nominee_full_name: [
    "nominee_first_name",
    "nominee_middle_name",
    "nominee_last_name",
  ],
  nominee_relationship: ["nominee_relationship"],
  nominee_dob: ["nominee_date_of_birth"],
  nominee_date_of_birth: ["nominee_date_of_birth"],
  blood_group: ["blood_group"],
  emergency_relationship: ["emergency_contact_relationship"],
  emergency_full_name: [
    "emergency_contact_first_name",
    "emergency_contact_middle_name",
    "emergency_contact_last_name",
  ],
  emergency_phone: ["emergency_contact_phone_number"],
  emergency_alternative_mobile: ["emergency_contact_alternative_phone_number"],
  emergency_email: ["emergency_contact_email_address"],
  "doc-nischint": ["authorization"],

  // —— Declarations ——
  code_of_conduct: ["code_of_conduct"],
  it_policy: ["it_policy"],
  posh_policy: ["posh_policy"],
  "doc-emp-declaration": ["declaration"],
  "doc-bgv": ["letter_of_authorisation"],
};

/**
 * Resolve Jedi UI key → canonical field_name list (always an array).
 */
export const resolveCanonicalFieldNames = (uiFieldKey = "") => {
  const key = String(uiFieldKey || "").trim();
  if (!key) return [];

  if (JEDI_UI_TO_CANONICAL_FIELD_NAMES[key]) {
    return [...JEDI_UI_TO_CANONICAL_FIELD_NAMES[key]];
  }

  // Education docs (doc-edu-marksheet-0 / doc-edu-certificate-1) are resolved
  // with qualification context in buildApprovalFeedback — do not hardcode here.
  if (/^doc-edu-(?:marksheet|certificate)(?:-\d+)?$/.test(key)) {
    return [];
  }

  if (key.startsWith("doc-")) {
    return [key.replace(/^doc-/, "").replace(/-/g, "_")];
  }

  return [key];
};

/**
 * Extra reverse aliases: older / alternate API field_name → Jedi_HR UI key.
 */
export const CANONICAL_FIELD_NAME_ALIASES_TO_JEDI_UI = {
  // Legacy pre-standardization names
  personal_mobile_no: "phone_number",
  office_mobile_no: "alternative_phone_number",
  personal_email_id: "email_address",
  aadhaar: "doc-aadhaar",
  aadhaar_photo: "doc-aadhaar-photo",
  profile_photo: "doc-profile-photo",
  uan_number: "uan",
  bank_account_number: "account_number",
  bank_account_type: "account_type",
  bank_branch_address: "branch_address",
  ifsc_code: "ifsc_code",
  pennyless_verified_at: "bank_verified_at",
  nischint_nominee_name: "nominee_full_name",
  nischint_nominee_relationship: "nominee_relationship",
  nischint_nominee_date_of_birth: "nominee_dob",
  emergency_contact_name: "emergency_full_name",
  emergency_contact_mobile: "emergency_phone",
  emergency_contact_email: "emergency_email",
  emergency_contact_relationship: "emergency_relationship",
  emergency_contact_alternative_mobile: "emergency_alternative_mobile",
  pancard: "doc-pan",
  bankdoc: "doc-bank",
  nischint: "doc-nischint",
  pf_authorization: "doc-pf-authorization",
  bgv_authorization: "doc-bgv",
  employee_declaration: "doc-emp-declaration",
  marksheets: "doc-edu-marksheet",
  marksheet: "doc-edu-marksheet",
  certificate: "doc-edu-certificate",
  graduation_certificate: "doc-edu-certificate",
  consolidated_marksheet: "doc-edu-marksheet",
  college: "college",
  same_as_permanent: "same_as_current",
  nominee_name: "nominee_full_name",
  emergency_name: "emergency_full_name",
  pan_card: "doc-pan",
  cancelled_cheque_bank_statement: "doc-bank",
  resignation_letter: "doc-resignation",
  relieving_letter: "doc-relieving",
  epfo_passbook: "doc-epfo",
  form2: "doc-form2",
  form11: "doc-form11",
  formf: "doc-formf",
  declaration: "doc-emp-declaration",
  letter_of_authorisation: "doc-bgv",
  photo: "doc-profile-photo",
  aadhaar_card: "doc-aadhaar",
  this_is_my_last_experience: "is_last_experience",
  name_as_per_pan: "account_holder_name",
  ifsc: "ifsc_code",
  pan: "pan_number",
  nominee_date_of_birth: "nominee_dob",
  college_institute: "college",

  // Short / legacy address names → the matching permanent or current field
  house: "current_address_house",
  street: "current_address_street",
  area: "current_address_area",
  landmark: "current_address_landmark",
  city: "current_address_city",
  state: "current_address_state",
  pincode: "current_address_pincode",
  permanent_house: "permanent_address_house",
  permanent_street: "permanent_address_street",
  permanent_area: "permanent_address_area",
  permanent_landmark: "permanent_address_landmark",
  current_address_house: "current_address_house",
  current_address_street: "current_address_street",
  current_address_area: "current_address_area",
  current_address_landmark: "current_address_landmark",
  current_address_city: "current_address_city",
  current_address_state: "current_address_state",
  current_address_pincode: "current_address_pincode",
  permanent_address_house: "permanent_address_house",
  permanent_address_street: "permanent_address_street",
  permanent_address_area: "permanent_address_area",
  permanent_address_landmark: "permanent_address_landmark",
  permanent_address_city: "permanent_address_city",
  permanent_address_state: "permanent_address_state",
  permanent_address_pincode: "permanent_address_pincode",

  // Nominee / emergency name parts → Jedi combined full-name fields
  nominee_first_name: "nominee_full_name",
  nominee_middle_name: "nominee_full_name",
  nominee_last_name: "nominee_full_name",
  emergency_first_name: "emergency_full_name",
  emergency_middle_name: "emergency_full_name",
  emergency_last_name: "emergency_full_name",
  emergency_contact_first_name: "emergency_full_name",
  emergency_contact_middle_name: "emergency_full_name",
  emergency_contact_last_name: "emergency_full_name",
  emergency_contact_phone_number: "emergency_phone",
  emergency_contact_alternative_phone_number: "emergency_alternative_mobile",
  emergency_contact_email_address: "emergency_email",
  emergency_phone_number: "emergency_phone",
  emergency_alternative_phone_number: "emergency_alternative_mobile",
  emergency_email_address: "emergency_email",
  emergency_relationship: "emergency_relationship",
};
